import React, { useState, useEffect } from 'react';
import { 
 UserCircleIcon, 
 XMarkIcon, 
 ArrowDownTrayIcon,
 CodeBracketIcon,
 CheckCircleIcon,
 PhoneIcon,
 IdentificationIcon,
 HomeIcon,
 AcademicCapIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const getAuthHeaders = (token) => ({
 'Authorization': `Bearer ${token}`,
 'Content-Type': 'application/json'
});

const ApplicationManagement = () => {
 const { token } = useAuth();
 const { showSuccess, showError } = useToast();
 const [applications, setApplications] = useState([]);
 const [loading, setLoading] = useState(true);
 const [programs, setPrograms] = useState([]);
 const [filters, setFilters] = useState({
 program_id: '',
 status: ''
 });
 const [updatingStatus, setUpdatingStatus] = useState(null);
 const [showExportModal, setShowExportModal] = useState(false);
 const [showRejectionModal, setShowRejectionModal] = useState(false);
 const [rejectionReason, setRejectionReason] = useState('');
 const [applicationToReject, setApplicationToReject] = useState(null);
 const [selectedCandidate, setSelectedCandidate] = useState(null);
 const [showCandidateModal, setShowCandidateModal] = useState(false);
 const [degreeTypes, setDegreeTypes] = useState([]);
 const [fieldsOfStudy, setFieldsOfStudy] = useState([]);

 useEffect(() => {
 fetchApplications();
 fetchPrograms();
 fetchDegreeTypes();
 fetchFieldsOfStudy();
 }, [filters]);

 const fetchPrograms = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/university/programs`, {
 headers: getAuthHeaders(token)
 });
 if (response.ok) {
 const data = await response.json();
 setPrograms(data);
 }
 } catch (error) {
 console.error('Error fetching programs:', error);
 }
 };

 const fetchDegreeTypes = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/degree-types`);
 if (response.ok) {
 const data = await response.json();
 setDegreeTypes(data);
 }
 } catch (error) {
 console.error('Error fetching degree types:', error);
 }
 };

 const fetchFieldsOfStudy = async () => {
 try {
 const response = await fetch(`${API_BASE_URL}/fields-of-study`);
 if (response.ok) {
 const data = await response.json();
 setFieldsOfStudy(data);
 }
 } catch (error) {
 console.error('Error fetching fields of study:', error);
 }
 };

 const fetchApplications = async () => {
 try {
 setLoading(true);
 const params = new URLSearchParams();
 if (filters.program_id) params.append('program_id', filters.program_id);
 if (filters.status) params.append('status', filters.status);

 const response = await fetch(
 `${API_BASE_URL}/university/applications?${params.toString()}`,
 {
 headers: getAuthHeaders(token)
 }
 );

 if (response.ok) {
 const data = await response.json();
 // Sort applications: Accepted first, Under_Review middle, Rejected last
 const sortedApplications = sortApplications(data.applications || []);
 setApplications(sortedApplications);
 } else {
 showError('Failed to load applications');
 }
 } catch (error) {
 console.error('Error fetching applications:', error);
 showError('Error loading applications');
 } finally {
 setLoading(false);
 }
 };

 const sortApplications = (apps) => {
 const statusOrder = { 'Accepted': 1, 'Under Review': 2, 'Pending': 3, 'Rejected': 4 };
 return [...apps].sort((a, b) => {
 const orderA = statusOrder[a.status] || 5;
 const orderB = statusOrder[b.status] || 5;
 if (orderA !== orderB) return orderA - orderB;
 // If same status, sort by submission date (newest first)
 return new Date(b.submitted_at) - new Date(a.submitted_at);
 });
 };

 const handleStatusUpdate = async (applicationId, newStatus, rejectionReason = null) => {
 try {
 setUpdatingStatus(applicationId);

 const requestBody = { status: newStatus };
 if (newStatus === 'Rejected' && rejectionReason) {
 requestBody.rejection_reason = rejectionReason;
 }

 const response = await fetch(
 `${API_BASE_URL}/university/applications/${applicationId}/status`,
 {
 method: 'PATCH',
 headers: getAuthHeaders(token),
 body: JSON.stringify(requestBody)
 }
 );

 if (response.ok) {
 showSuccess(`Application ${newStatus.replace('_', ' ').toLowerCase()} successfully`);
 fetchApplications(); // Refresh and re-sort the list
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to update status');
 }
 } catch (error) {
 console.error('Error updating status:', error);
 showError(`Error updating application status: ${error.message}`);
 } finally {
 setUpdatingStatus(null);
 }
 };

 const handleAcceptClick = (applicationId) => {
 handleStatusUpdate(applicationId, 'Accepted');
 };

 const handleRejectClick = (applicationId) => {
 setApplicationToReject(applicationId);
 setRejectionReason('');
 setShowRejectionModal(true);
 };

 const handleRejectConfirm = async () => {
 if (!rejectionReason.trim()) {
 showError('Please provide a reason for rejection');
 return;
 }
 
 await handleStatusUpdate(applicationToReject, 'Rejected', rejectionReason);
 setShowRejectionModal(false);
 setApplicationToReject(null);
 setRejectionReason('');
 };

 const handleRejectCancel = () => {
 setShowRejectionModal(false);
 setApplicationToReject(null);
 setRejectionReason('');
 };

 const handleViewCandidate = async (candidateId, applicationId, currentStatus) => {
 try {
 // If status is Pending, automatically change it to Under Review (with space, not underscore)
 if (currentStatus === 'Pending') {
 await handleStatusUpdate(applicationId, 'Under Review');
 }

 const response = await fetch(
 `${API_BASE_URL}/university/candidate/${candidateId}`,
 {
 headers: getAuthHeaders(token)
 }
 );

 if (response.ok) {
 const data = await response.json();
 // Add application_id to the candidate data
 setSelectedCandidate({ ...data, application_id: applicationId });
 setShowCandidateModal(true);
 } else {
 showError('Failed to load candidate profile');
 }
 } catch (error) {
 console.error('Error fetching candidate:', error);
 showError('Error loading candidate profile');
 }
 };

 const getDegreeTypeName = (id) => {
 if (!id) return 'Unknown';
 if (degreeTypes.length === 0) return 'Loading...';
 const degreeType = degreeTypes.find(type => type.id === parseInt(id));
 return degreeType ? degreeType.name : `Degree Type ID ${id}`;
 };

 const getFieldOfStudyName = (id) => {
 if (!id) return 'Unknown';
 if (fieldsOfStudy.length === 0) return 'Loading...';
 const field = fieldsOfStudy.find(f => f.id === parseInt(id));
 return field ? field.name : `Field ID ${id}`;
 };

 const getStatusBadgeClass = (status) => {
 switch (status) {
 case 'Pending':
 return 'bg-yellow-100 text-yellow-800';
 case 'Under Review':
 return 'bg-blue-100 text-blue-800';
 case 'Accepted':
 return 'bg-green-100 text-green-800';
 case 'Rejected':
 return 'bg-red-100 text-red-800';
 default:
 return 'bg-slate-100 text-slate-800';
 }
 };

 return (
 <div className="space-y-6">
 <div className="card relative overflow-hidden">
 <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
 <div>
 <h3 className="text-lg font-bold text-slate-800">Student Applications</h3>
 <p className="text-sm text-slate-500 mt-1">
 {applications.length} application{applications.length !== 1 ? 's' : ''} received
 </p>
 </div>
 <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
 <select
 value={filters.program_id}
 onChange={(e) => setFilters({ ...filters, program_id: e.target.value })}
 className="border border-slate-200 bg-white text-slate-700 rounded-xl pl-4 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold cursor-pointer"
 >
 <option value="">All Programs</option>
 {programs.map((program) => (
 <option key={program.id} value={program.id}>
 {program.program_name}
 </option>
 ))}
 </select>
 <select
 value={filters.status}
 onChange={(e) => setFilters({ ...filters, status: e.target.value })}
 className="border border-slate-200 bg-white text-slate-700 rounded-xl pl-4 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 font-semibold cursor-pointer"
 >
 <option value="">All Status</option>
 <option value="Pending">Pending</option>
 <option value="Under Review">Under Review</option>
 <option value="Accepted">Accepted</option>
 <option value="Rejected">Rejected</option>
 </select>
 <button
 onClick={() => setShowExportModal(true)}
 className="btn-primary inline-flex items-center text-sm py-2 px-5 justify-center w-full sm:w-auto shrink-0"
 >
 <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
 <span>Export</span>
 </button>
 </div>
 </div>

 {loading ? (
 <div className="text-center py-12">
 <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
 <p className="text-slate-500 mt-4 text-sm font-semibold">Loading applications...</p>
 </div>
 ) : applications.length === 0 ? (
 <div className="text-center py-12 text-slate-400 bg-slate-50 border border-slate-100 rounded-2xl">
 <UserCircleIcon className="h-16 w-16 mx-auto mb-4 text-slate-300 animate-pulse" />
 <p className="font-bold text-slate-700 text-lg">No Applications Found</p>
 <p className="text-sm mt-1 max-w-sm mx-auto font-medium">
 {filters.program_id || filters.status
 ? 'Try adjusting your filter preferences above.'
 : 'When students apply to your programs, they will appear here.'}
 </p>
 </div>
 ) : (
 <div className="overflow-x-auto rounded-2xl border border-slate-200">
 <table className="min-w-full divide-y divide-slate-100 bg-white">
 <thead className="bg-slate-50">
 <tr>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Student
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Program
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Status
 </th>
 <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Submitted
 </th>
 <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
 Actions
 </th>
 </tr>
 </thead>
 <tbody className="divide-y divide-slate-100 bg-white">
 {applications.map((application) => (
 <tr key={application.id} className="hover:bg-slate-50 transition-colors">
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="flex items-center">
 <div className="flex-shrink-0 h-10 w-10">
 <div className="h-10 w-10 rounded-full bg-primary-50 border border-primary-100 flex items-center justify-center">
 <span className="text-primary-600 font-bold text-sm">
 {application.student_name?.charAt(0) || 'S'}
 </span>
 </div>
 </div>
 <div className="ml-4">
 <div className="text-sm font-bold text-slate-800">
 {application.student_name}
 </div>
 <button
 onClick={() => handleViewCandidate(application.candidate_id, application.id, application.status)}
 className="text-xs text-primary-600 hover:text-primary-700 font-semibold hover:underline"
 >
 View Candidate Profile
 </button>
 </div>
 </div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <div className="text-sm text-slate-600 font-semibold">{application.program_name}</div>
 </td>
 <td className="px-6 py-4 whitespace-nowrap">
 <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-bold rounded-lg border ${
 application.status === 'Pending' ? 'bg-amber-50 border border-amber-200/50 text-amber-700' :
 application.status === 'Under Review' || application.status === 'Under_Review' ? 'bg-blue-50 border border-blue-200/50 text-blue-700' :
 application.status === 'Accepted' ? 'bg-emerald-50 border border-emerald-200/50 text-emerald-700' :
 application.status === 'Rejected' ? 'bg-rose-50 border border-rose-200/50 text-rose-700' :
 'bg-slate-50 border border-slate-200 text-slate-600'
 }`}>
 {application.status.replace('_', ' ')}
 </span>
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 font-medium">
 {new Date(application.submitted_at).toLocaleDateString()}
 </td>
 <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
 <div className="flex justify-end space-x-2">
 {(application.status === 'Pending' || application.status === 'Under Review') && (
 <>
 <button
 onClick={() => handleViewCandidate(application.candidate_id, application.id, application.status)}
 className="btn-secondary py-1 px-3 text-xs"
 >
 Review
 </button>
 <button
 onClick={() => handleAcceptClick(application.id)}
 disabled={updatingStatus === application.id}
 className="inline-flex items-center px-3 py-1 text-xs font-bold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 shadow-sm transition-all active:scale-[0.98] cursor-pointer"
 >
 <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
 Accept
 </button>
 <button
 onClick={() => handleRejectClick(application.id)}
 disabled={updatingStatus === application.id}
 className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-lg text-rose-600 bg-rose-50 border border-rose-100 hover:bg-rose-100 hover:text-rose-700 disabled:opacity-50 transition-all active:scale-[0.98] cursor-pointer"
 >
 Reject
 </button>
 </>
 )}
 {application.status === 'Accepted' && (
 <span className="text-xs text-emerald-600 font-bold bg-emerald-50 border border-emerald-100 rounded-lg px-2.5 py-0.5">Accepted</span>
 )}
 {application.status === 'Rejected' && (
 <span className="text-xs text-rose-600 font-bold bg-rose-50 border border-rose-100 rounded-lg px-2.5 py-0.5">Rejected</span>
 )}
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 )}
 </div>

 {/* Rejection Modal */}
 {showRejectionModal && (
 <div className="fixed inset-0 bg-slate-900/60 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4 animate-fade-in">
 <div className="bg-white rounded-2xl max-w-md w-full border border-slate-200 shadow-2xl relative p-6">
 <div className="flex justify-between items-center mb-4">
 <h3 className="text-lg font-bold text-slate-800">Reject Application</h3>
 <button
 onClick={handleRejectCancel}
 className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors"
 >
 <XMarkIcon className="h-6 w-6" />
 </button>
 </div>
 <div className="mb-6">
 <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
 Reason for Rejection
 </label>
 <textarea
 value={rejectionReason}
 onChange={(e) => setRejectionReason(e.target.value)}
 rows="4"
 className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 bg-white text-slate-700 transition-all duration-300"
 placeholder="Please provide a reason for rejecting this application..."
 />
 </div>
 <div className="flex justify-end space-x-3 border-t border-slate-100 pt-4">
 <button
 onClick={handleRejectCancel}
 className="btn-secondary py-2 px-5 text-sm"
 >
 Cancel
 </button>
 <button
 onClick={handleRejectConfirm}
 className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-bold py-2 px-5 rounded-xl border border-transparent shadow-md shadow-red-500/10 hover:shadow-lg hover:shadow-red-500/20 active:scale-[0.98] transition-all duration-300 ease-out text-sm"
 >
 Confirm Rejection
 </button>
 </div>
 </div>
 </div>
 )}

 {/* Export Modal */}
 {showExportModal && (
 <ExportModal
 onClose={() => setShowExportModal(false)}
 token={token}
 showSuccess={showSuccess}
 showError={showError}
 />
 )}

 {/* Candidate Profile Modal */}
 {showCandidateModal && selectedCandidate && (
 <CandidateProfileModal
 selectedCandidate={selectedCandidate}
 onClose={() => setShowCandidateModal(false)}
 getDegreeTypeName={getDegreeTypeName}
 getFieldOfStudyName={getFieldOfStudyName}
 />
 )}
 </div>
 );
};

// Export Modal Component
const ExportModal = ({ onClose, token, showSuccess, showError }) => {
 const [selectedFields, setSelectedFields] = useState([
 'basic_information',
 'contact_details',
 'demographics',
 'program_preferences',
 'academic_records'
 ]);
 const [statusFilter, setStatusFilter] = useState(['Accepted', 'Under Review', 'Rejected']);
 const [exportType, setExportType] = useState('download'); // 'download' or 'api'
 const [exportFormat, setExportFormat] = useState('csv'); // 'csv' or 'excel'
 const [exporting, setExporting] = useState(false);
 const [apiData, setApiData] = useState(null);

 const fieldOptions = [
 { id: 'basic_information', label: 'Basic Information' },
 { id: 'contact_details', label: 'Contact Details' },
 { id: 'demographics', label: 'Demographics' },
 { id: 'guardian_details', label: 'Guardian Details' },
 { id: 'program_preferences', label: 'Program Preferences' },
 { id: 'academic_records', label: 'Academic Records' },
 { id: 'documents', label: 'Documents' }
 ];

 const statusOptions = [
 { id: 'Accepted', label: 'Accepted' },
 { id: 'Under Review', label: 'Under Review' },
 { id: 'Rejected', label: 'Rejected' }
 ];

 const toggleField = (fieldId) => {
 setSelectedFields(prev =>
 prev.includes(fieldId)
 ? prev.filter(f => f !== fieldId)
 : [...prev, fieldId]
 );
 };

 const toggleStatus = (statusId) => {
 setStatusFilter(prev =>
 prev.includes(statusId)
 ? prev.filter(s => s !== statusId)
 : [...prev, statusId]
 );
 };

 const handleDownload = async () => {
 try {
 setExporting(true);
 const response = await fetch(`${API_BASE_URL}/university/applications/export`, {
 method: 'POST',
 headers: getAuthHeaders(token),
 body: JSON.stringify({
 format: exportFormat,
 selected_fields: selectedFields,
 status_filter: statusFilter
 })
 });

 if (response.ok) {
 const blob = await response.blob();
 const url = window.URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = `applications_export_${new Date().toISOString().split('T')[0]}.${exportFormat === 'csv' ? 'csv' : 'xlsx'}`;
 document.body.appendChild(a);
 a.click();
 window.URL.revokeObjectURL(url);
 document.body.removeChild(a);
 showSuccess('Applications exported successfully');
 onClose();
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to export applications');
 }
 } catch (error) {
 console.error('Export error:', error);
 showError('Error exporting applications');
 } finally {
 setExporting(false);
 }
 };

 const handleGetAPI = async () => {
 try {
 setExporting(true);
 const response = await fetch(`${API_BASE_URL}/university/applications/api-data`, {
 method: 'POST',
 headers: getAuthHeaders(token),
 body: JSON.stringify({
 selected_fields: selectedFields,
 status_filter: statusFilter
 })
 });

 if (response.ok) {
 const data = await response.json();
 setApiData(data);
 showSuccess('API data retrieved successfully');
 } else {
 const error = await response.json();
 showError(error.error || 'Failed to retrieve API data');
 }
 } catch (error) {
 console.error('API data error:', error);
 showError('Error retrieving API data');
 } finally {
 setExporting(false);
 }
 };

 const copyToClipboard = () => {
 navigator.clipboard.writeText(JSON.stringify(apiData, null, 2));
 showSuccess('API data copied to clipboard');
 };

 return (
 <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4 animate-fade-in">
 <div className="relative mx-auto border border-slate-200 w-full max-w-2xl shadow-2xl rounded-2xl bg-white overflow-hidden max-h-[90vh] flex flex-col">
 <div className="flex justify-between items-center p-6 border-b border-slate-100">
 <h3 className="text-xl font-bold text-slate-800 tracking-tight">Export Applications</h3>
 <button
 onClick={onClose}
 className="text-slate-400 hover:text-slate-650 p-2 hover:bg-slate-50 rounded-xl transition-all"
 >
 <XMarkIcon className="h-5 w-5" />
 </button>
 </div>

 <div className="p-6 sm:p-8 space-y-6 overflow-y-auto custom-scrollbar">
 {/* Export Type Selection */}
 <div>
 <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Export Type</label>
 <div className="flex space-x-4">
 <button
 onClick={() => setExportType('download')}
 className={`flex-1 py-4 px-4 border rounded-xl text-sm font-bold flex flex-col items-center justify-center transition-all active:scale-[0.98] ${
 exportType === 'download'
 ? 'border-primary-500 bg-primary-50/50 text-primary-700 shadow-sm shadow-primary-500/5'
 : 'border-slate-200 bg-white text-slate-650 hover:bg-slate-50 hover:border-slate-300'
 }`}
 >
 <ArrowDownTrayIcon className="h-5 w-5 mb-1.5" />
 Download File
 </button>
 <button
 onClick={() => setExportType('api')}
 className={`flex-1 py-4 px-4 border rounded-xl text-sm font-bold flex flex-col items-center justify-center transition-all active:scale-[0.98] ${
 exportType === 'api'
 ? 'border-primary-500 bg-primary-50/50 text-primary-700 shadow-sm shadow-primary-500/5'
 : 'border-slate-200 bg-white text-slate-650 hover:bg-slate-50 hover:border-slate-300'
 }`}
 >
 <CodeBracketIcon className="h-5 w-5 mb-1.5" />
 Get API Data
 </button>
 </div>
 </div>

 {/* Format Selection (only for download) */}
 {exportType === 'download' && (
 <div>
 <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">File Format</label>
 <div className="flex space-x-4">
 <button
 onClick={() => setExportFormat('csv')}
 className={`flex-1 py-2.5 px-4 border rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${
 exportFormat === 'csv'
 ? 'border-primary-500 bg-primary-50/50 text-primary-700 shadow-sm'
 : 'border-slate-200 bg-white text-slate-650 hover:bg-slate-50 hover:border-slate-300'
 }`}
 >
 CSV
 </button>
 <button
 onClick={() => setExportFormat('excel')}
 className={`flex-1 py-2.5 px-4 border rounded-xl text-sm font-bold transition-all active:scale-[0.98] ${
 exportFormat === 'excel'
 ? 'border-primary-500 bg-primary-50/50 text-primary-700 shadow-sm'
 : 'border-slate-200 bg-white text-slate-650 hover:bg-slate-50 hover:border-slate-300'
 }`}
 >
 Excel
 </button>
 </div>
 </div>
 )}

 {/* Field Selection */}
 <div>
 <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Select Fields to Export</label>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
 {fieldOptions.map((field) => (
 <label key={field.id} className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
 <input
 type="checkbox"
 checked={selectedFields.includes(field.id)}
 onChange={() => toggleField(field.id)}
 className="rounded-lg border-slate-300 text-primary-600 focus:ring-primary-500/20 focus:border-primary-500 h-4.5 w-4.5 cursor-pointer transition-all"
 />
 <span className="text-sm font-semibold text-slate-700">{field.label}</span>
 </label>
 ))}
 </div>
 </div>

 {/* Status Filter */}
 <div>
 <label className="block text-xs font-bold text-slate-500 mb-3 uppercase tracking-wider">Filter by Status</label>
 <div className="flex flex-wrap gap-3">
 {statusOptions.map((status) => (
 <label key={status.id} className="flex items-center space-x-3 cursor-pointer p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-50 transition-colors flex-1 min-w-[120px]">
 <input
 type="checkbox"
 checked={statusFilter.includes(status.id)}
 onChange={() => toggleStatus(status.id)}
 className="rounded-lg border-slate-300 text-primary-600 focus:ring-primary-500/20 focus:border-primary-500 h-4.5 w-4.5 cursor-pointer transition-all"
 />
 <span className="text-sm font-semibold text-slate-700">{status.label}</span>
 </label>
 ))}
 </div>
 </div>

 {/* API Data Display */}
 {apiData && exportType === 'api' && (
 <div className="space-y-2">
 <div className="flex justify-between items-center">
 <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">API Response</label>
 <button
 onClick={copyToClipboard}
 className="text-xs text-primary-600 hover:text-primary-700 font-bold transition-colors"
 >
 Copy to Clipboard
 </button>
 </div>
 <div className="bg-slate-900 border border-slate-950 rounded-xl p-4 max-h-64 overflow-y-auto shadow-inner">
 <pre className="font-mono text-xs text-slate-300 whitespace-pre-wrap">
 {JSON.stringify(apiData, null, 2)}
 </pre>
 </div>
 <p className="text-xs text-slate-400 font-medium">
 Total Records: <span className="font-bold text-slate-600">{apiData.total_count}</span> | Exported At: <span className="font-bold text-slate-650">{new Date(apiData.exported_at).toLocaleString()}</span>
 </p>
 </div>
 )}
 </div>

 {/* Action Buttons */}
 <div className="flex justify-end space-x-3 p-6 border-t border-slate-100 bg-slate-50">
 <button
 onClick={onClose}
 className="btn-secondary px-5 py-2.5 text-sm"
 >
 Cancel
 </button>
 {exportType === 'download' ? (
 <button
 onClick={handleDownload}
 disabled={exporting || selectedFields.length === 0 || statusFilter.length === 0}
 className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50 disabled:pointer-events-none"
 >
 {exporting ? 'Exporting...' : 'Download'}
 </button>
 ) : (
 <button
 onClick={handleGetAPI}
 disabled={exporting || selectedFields.length === 0 || statusFilter.length === 0}
 className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50 disabled:pointer-events-none"
 >
 {exporting ? 'Loading...' : 'Get API Data'}
 </button>
 )}
 </div>
 </div>
 </div>
 );
};

// Candidate Profile Modal Component
const CandidateProfileModal = ({ selectedCandidate, onClose, getDegreeTypeName, getFieldOfStudyName }) => {
 const { token } = useAuth();
 const [additionalResponses, setAdditionalResponses] = useState([]);
 const [loadingResponses, setLoadingResponses] = useState(true);

 useEffect(() => {
 if (selectedCandidate?.application_id) {
 fetchAdditionalResponses();
 }
 }, [selectedCandidate]);

 const fetchAdditionalResponses = async () => {
 try {
 setLoadingResponses(true);
 const response = await fetch(
 `${API_BASE_URL}/additional-admission/responses/application/${selectedCandidate.application_id}`,
 {
 headers: getAuthHeaders(token)
 }
 );

 if (response.ok) {
 const data = await response.json();
 setAdditionalResponses(data.responses || []);
 } else if (response.status === 404) {
 // No responses - that's okay
 setAdditionalResponses([]);
 }
 } catch (error) {
 console.error('Error fetching additional responses:', error);
 setAdditionalResponses([]);
 } finally {
 setLoadingResponses(false);
 }
 };

 return (
 <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center z-50 p-4 animate-fade-in">
 <div className="relative mx-auto border border-slate-200 w-full max-w-5xl shadow-2xl rounded-2xl bg-white overflow-hidden max-h-[90vh] flex flex-col">
 <div className="flex justify-between items-center p-6 border-b border-slate-100">
 <h3 className="text-xl font-bold text-slate-800 tracking-tight">Candidate Profile</h3>
 <button
 onClick={onClose}
 className="text-slate-400 hover:text-slate-650 p-2 hover:bg-slate-50 rounded-xl transition-all"
 >
 <XMarkIcon className="h-5 w-5" />
 </button>
 </div>

 <div className="p-6 sm:p-8 space-y-6 overflow-y-auto custom-scrollbar flex-1">
 {/* Basic Info */}
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <UserCircleIcon className="h-5 w-5 mr-2 text-primary-600" />
 Basic Information
 </h4>

 <div className="flex items-center gap-5">
 {selectedCandidate.demographics?.profile_picture_url ? (
 <img
 src={selectedCandidate.demographics.profile_picture_url}
 alt="Profile"
 className="w-20 h-20 rounded-2xl object-cover border-2 border-white shadow-md shrink-0 bg-white"
 />
 ) : (
 <div className="w-20 h-20 rounded-2xl bg-slate-100 border-2 border-slate-200 flex items-center justify-center shrink-0 shadow-inner">
 <UserCircleIcon className="h-10 w-10 text-slate-400" />
 </div>
 )}
 <div>
 <p className="text-lg font-bold text-slate-800">{selectedCandidate.student_name}</p>
 <p className="text-sm text-slate-400 mt-1 font-medium">
 CNIC: <span className="font-bold text-slate-700">{selectedCandidate.cnic}</span>
 </p>
 </div>
 </div>
 </div>

 {/* Contact Details */}
 {selectedCandidate.contact_details && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <PhoneIcon className="h-5 w-5 mr-2 text-primary-600" />
 Contact Details
 </h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
 {selectedCandidate.contact_details.email && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Email Address</p>
 <p className="font-bold text-slate-750 mt-0.5 text-sm truncate">{selectedCandidate.contact_details.email}</p>
 </div>
 )}
 {selectedCandidate.contact_details.mobile_number && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mobile Number</p>
 <p className="font-bold text-slate-750 mt-0.5 text-sm">{selectedCandidate.contact_details.mobile_number}</p>
 </div>
 )}
 {selectedCandidate.contact_details.alternative_email && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Alternative Email</p>
 <p className="font-bold text-slate-755 mt-0.5 text-sm truncate">{selectedCandidate.contact_details.alternative_email}</p>
 </div>
 )}
 {selectedCandidate.contact_details.alternative_mobile_number && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Alternative Mobile</p>
 <p className="font-bold text-slate-750 mt-0.5 text-sm">{selectedCandidate.contact_details.alternative_mobile_number}</p>
 </div>
 )}
 {selectedCandidate.contact_details.telephone_no && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Telephone Number</p>
 <p className="font-bold text-slate-750 mt-0.5 text-sm">{selectedCandidate.contact_details.telephone_no}</p>
 </div>
 )}
 {selectedCandidate.contact_details.emergency_contact && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Emergency Contact</p>
 <p className="font-bold text-slate-750 mt-0.5 text-sm">{selectedCandidate.contact_details.emergency_contact}</p>
 </div>
 )}
 {selectedCandidate.contact_details.current_address && (
 <div className="md:col-span-2">
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Current Address</p>
 <p className="font-bold text-slate-705 mt-0.5 text-sm leading-relaxed">{selectedCandidate.contact_details.current_address}</p>
 </div>
 )}
 {selectedCandidate.contact_details.permanent_address && (
 <div className="md:col-span-2">
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Permanent Address</p>
 <p className="font-bold text-slate-705 mt-0.5 text-sm leading-relaxed">{selectedCandidate.contact_details.permanent_address}</p>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Demographics */}
 {selectedCandidate.demographics && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <IdentificationIcon className="h-5 w-5 mr-2 text-primary-600" />
 Demographics
 </h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
 {selectedCandidate.demographics.date_of_birth && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Date of Birth</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.date_of_birth}</p>
 </div>
 )}
 {selectedCandidate.demographics.gender && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Gender</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.gender}</p>
 </div>
 )}
 {selectedCandidate.demographics.nationality && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Nationality</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.nationality}</p>
 </div>
 )}
 {selectedCandidate.demographics.religion && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Religion</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.religion}</p>
 </div>
 )}
 {selectedCandidate.demographics.place_of_birth && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Place of Birth</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.place_of_birth}</p>
 </div>
 )}
 {selectedCandidate.demographics.marital_status && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Marital Status</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.marital_status}</p>
 </div>
 )}
 {selectedCandidate.demographics.last_institute_attended && (
 <div className="sm:col-span-2">
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Last Institute Attended</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm truncate">{selectedCandidate.demographics.last_institute_attended}</p>
 </div>
 )}
 {selectedCandidate.demographics.husband_name && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Husband's Name</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.demographics.husband_name}</p>
 </div>
 )}
 {selectedCandidate.contact_details?.country && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Country</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.contact_details.country}</p>
 </div>
 )}
 {selectedCandidate.contact_details?.state_province && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">State / Province</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.contact_details.state_province}</p>
 </div>
 )}
 {selectedCandidate.contact_details?.city && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">City</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.contact_details.city}</p>
 </div>
 )}
 {selectedCandidate.contact_details?.postal_code && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Postal Code</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.contact_details.postal_code}</p>
 </div>
 )}
 {selectedCandidate.contact_details?.domicile && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Domicile City</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.contact_details.domicile}</p>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Guardian Details */}
 {selectedCandidate.guardian_details && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <HomeIcon className="h-5 w-5 mr-2 text-primary-600" />
 Guardian Details
 </h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
 {(selectedCandidate.guardian_details.father_first_name || selectedCandidate.guardian_details.father_last_name) && (
 <div className="sm:col-span-2 md:col-span-3">
 <h5 className="text-xs font-bold text-primary-600 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Father's Details</h5>
 </div>
 )}
 {(selectedCandidate.guardian_details.father_first_name || selectedCandidate.guardian_details.father_last_name) && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Name</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">
 {[selectedCandidate.guardian_details.father_first_name, selectedCandidate.guardian_details.father_last_name].filter(Boolean).join(' ')}
 </p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_cnic && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's CNIC</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.father_cnic}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_occupation && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Occupation</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.father_occupation}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_organization_name && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Organization</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm truncate">{selectedCandidate.guardian_details.father_organization_name}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_designation && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Designation</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm truncate">{selectedCandidate.guardian_details.father_designation}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_email && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Email</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm truncate">{selectedCandidate.guardian_details.father_email}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_cell && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Cell Number</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.father_cell}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_ntn_no && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's NTN</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.father_ntn_no}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.father_office_address && (
 <div className="sm:col-span-2 md:col-span-3">
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Father's Office Address</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm leading-relaxed">{selectedCandidate.guardian_details.father_office_address}</p>
 </div>
 )}

 {(selectedCandidate.guardian_details.mother_first_name || selectedCandidate.guardian_details.mother_last_name) && (
 <div className="sm:col-span-2 md:col-span-3 mt-4">
 <h5 className="text-xs font-bold text-primary-600 uppercase tracking-wider border-b border-slate-200 pb-1 mb-2">Mother's Details</h5>
 </div>
 )}
 {(selectedCandidate.guardian_details.mother_first_name || selectedCandidate.guardian_details.mother_last_name) && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mother's Name</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">
 {[selectedCandidate.guardian_details.mother_first_name, selectedCandidate.guardian_details.mother_last_name].filter(Boolean).join(' ')}
 </p>
 </div>
 )}
 {selectedCandidate.guardian_details.mother_cnic && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mother's CNIC</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.mother_cnic}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.mother_cell && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mother's Cell Number</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.mother_cell}</p>
 </div>
 )}
 {selectedCandidate.guardian_details.number_of_siblings !== undefined && selectedCandidate.guardian_details.number_of_siblings !== null && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Number of Siblings</p>
 <p className="font-bold text-slate-700 mt-0.5 text-sm">{selectedCandidate.guardian_details.number_of_siblings}</p>
 </div>
 )}
 </div>
 </div>
 )}

 {/* Program Preferences */}
 {selectedCandidate.program_preferences && selectedCandidate.program_preferences.length > 0 && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <AcademicCapIcon className="h-5 w-5 mr-2 text-primary-600" />
 Program Preferences
 </h4>
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
 {selectedCandidate.program_preferences.map((pref, index) => (
 <div key={index} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm hover:border-primary-200 transition-all flex items-start space-x-3">
 <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold bg-primary-50 border border-primary-200/50 text-primary-700 shadow-sm shadow-primary-500/5 shrink-0">
 Pref #{pref.preference_order || index + 1}
 </span>
 <div className="min-w-0">
 <p className="font-bold text-slate-700 text-sm">
 {pref.degree_type_name || getDegreeTypeName(pref.degree_type_id)}
 </p>
 <p className="text-xs text-slate-400 font-medium mt-1">
 Field: <span className="font-bold text-slate-650">{pref.field_of_study_name || getFieldOfStudyName(pref.field_of_study_id)}</span>
 </p>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Academic Records */}
 {selectedCandidate.academic_records && selectedCandidate.academic_records.length > 0 && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <AcademicCapIcon className="h-5 w-5 mr-2 text-primary-600" />
 Academic Records
 </h4>
 <div className="space-y-4">
 {selectedCandidate.academic_records.map((record, index) => (
 <div key={index} className="bg-white border border-slate-200/65 p-5 rounded-xl shadow-sm">
 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Qualification Level</p>
 <p className="font-bold text-slate-700 mt-0.5">{record.qualification_level}</p>
 </div>
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Institute Name</p>
 <p className="font-bold text-slate-700 mt-0.5 truncate">{record.institute_name}</p>
 </div>
 {record.board_name && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Board / University</p>
 <p className="font-bold text-slate-700 mt-0.5 truncate">{record.board_name}</p>
 </div>
 )}
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Passing Year</p>
 <p className="font-bold text-slate-750 mt-0.5">{record.passing_year}</p>
 </div>
 {record.discipline && (
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Discipline</p>
 <p className="font-bold text-slate-700 mt-0.5">{record.discipline}</p>
 </div>
 )}
 <div>
 <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Marks / Grade</p>
 <p className="font-bold text-slate-700 mt-0.5">
 {record.obtained_marks && record.total_marks ? (
 <>
 {record.obtained_marks} / {record.total_marks}
 {record.grading_type === 'Percentage' && (
 <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-emerald-50 border border-emerald-200/50 text-emerald-700 ml-2">
 {((parseFloat(record.obtained_marks) / parseFloat(record.total_marks)) * 100).toFixed(2)}%
 </span>
 )}
 </>
 ) : (
 'Not provided'
 )}
 </p>
 </div>
 </div>
 </div>
 ))}
 </div>
 </div>
 )}

 {/* Additional Admission Information */}
 {!loadingResponses && additionalResponses.length > 0 && (
 <div className="bg-slate-50 border border-slate-100 p-5 rounded-2xl shadow-sm">
 <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center">
 <AcademicCapIcon className="h-5 w-5 mr-2 text-primary-600" />
 Additional Admission Information
 </h4>
 <div className="space-y-4">
 {additionalResponses.map((response, index) => (
 <div key={index} className="bg-white border border-slate-200 p-4 rounded-xl shadow-sm">
 <p className="text-sm font-bold text-slate-700 flex items-center">
 {response.question_label}
 {response.is_required && <span className="text-red-500 ml-1 font-bold">*</span>}
 </p>
 <p className="text-sm text-slate-650 mt-2 bg-slate-50 p-3 rounded-lg border border-slate-100 font-medium">
 {response.response_value || <span className="text-slate-450 italic">No response</span>}
 </p>
 </div>
 ))}
 </div>
 </div>
 )}
 </div>

 {/* Footer */}
 <div className="flex justify-end p-6 border-t border-slate-100 bg-slate-50">
 <button
 onClick={onClose}
 className="btn-secondary px-6 py-2.5 text-sm"
 >
 Close Profile
 </button>
 </div>
 </div>
 </div>
 );
};

export default ApplicationManagement;
